// i18n Type Definitions

export type Language = 'ko' | 'en' | 'ja' | 'zh-CN' | 'zh-TW';

export interface LanguageInfo {
    code: Language;
    name: string;
    nativeName: string;
    locale: string; // for Intl.DateTimeFormat
}

export const LANGUAGES: LanguageInfo[] = [
    { code: 'ko', name: 'Korean', nativeName: '한국어', locale: 'ko-KR' },
    { code: 'en', name: 'English', nativeName: 'English', locale: 'en-US' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', locale: 'ja-JP' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', locale: 'zh-CN' },
    { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', locale: 'zh-TW' },
];

export const DEFAULT_LANGUAGE: Language = 'ko';

// Translation keys structure
export interface Translations {
    common: {
        save: string;
        cancel: string;
        delete: string;
        edit: string;
        add: string;
        loading: string;
        error: string;
        success: string;
        confirm: string;
        yes: string;
        no: string;
        search: string;
        noResults: string;
        refresh: string;
        logout: string;
        settings: string;
        download: string;
    };
    nav: {
        chat: string;
        gallery: string;
        settings: string;
        admin: string;
        users: string;
        models: string;
    };
    auth: {
        login: string;
        register: string;
        username: string;
        password: string;
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
        loginButton: string;
        registerButton: string;
        loggingIn: string;
        invalidCredentials: string;
        changePassword: string;
        changingPassword: string;
        passwordChanged: string;
        invalidateAllSessions: string;
        allSessionsInvalidated: string;
    };
    chat: {
        newChat: string;
        sendMessage: string;
        typeMessage: string;
        noChats: string;
        startNewChat: string;
        deleteChat: string;
        deleteChatConfirm: string;
        deleteMessages: string; // Added
        deleteMessageConfirm: string; // Added
        exportChat: string;
        importChat: string;
        selectModel: string;
        thinking: string;
    };
    gallery: {
        title: string;
        description: string;
        upload: string;
        dragDrop: string;
        delete: string;
        move: string;
        sync: string;
        noFiles: string;
        syncComplete: string;
        uploadComplete: string;
        deleteConfirm: string;
        newFolder: string;
        // Metadata viewer
        path: string;
        filename: string;
        no_metadata: string;
        model: string;
        seed: string;
        size: string;
        sampler: string;
        prompt: string;
        negative_prompt: string;
        ai_analyze: string;
        mode_image: string;
        mode_prompt: string;
        mode_both: string;
        apply_settings: string;
    };
    settings: {
        title: string;
        apiKey: string;
        apiKeyDesc: string;
        apiKeyPlaceholder: string;
        apiKeySaved: string;
        apiKeyRemoved: string;
        removeApiKey: string;
        r2Config: string;
        r2ConfigDesc: string;
        r2Configured: string;
        r2SaveSuccess: string;
        r2ChangeHint: string; // Added
        autoSave: string;
        autoSaveDesc: string;
        chatBackup: string;
        exportAll: string;
        importAll: string;
        backupDesc: string;
        backupSuccess: string;
        backupFailed: string;
        importSuccess: string;
        importFailed: string;
        security: string;
        securityDesc: string;
        passwordChangeHint: string; // Added
        sessionLogoutHint: string; // Added
        systemPrompts: string;
        addPrompt: string;
        promptTitle: string;
        promptContent: string;
        deletePromptConfirm: string;
        clearCache: string;
        clearCacheDesc: string;
        clearCacheConfirm: string;
        cacheClearedRefresh: string;
    };
    admin: {
        users: {
            title: string;
            description: string;
            addUser: string;
            username: string;
            password: string;
            role: string;
            admin: string;
            user: string;
            createdAt: string;
            actions: string;
            noUsers: string;
            deleteConfirm: string;
            createFailed: string;
            cannotDeleteSelf: string;
            cannotChangeOwnRole: string;
            passwordMinLength: string;
            passwordChanged: string;
            resetPassword: string;
            newPassword: string;
            enterNewPassword: string;
        };
        models: {
            title: string;
            description: string;
            fetchFromGoogle: string;
            fetchModels: string;
            noModels: string;
            noModelsDesc: string;
            active: string;
            inactive: string;
            modelsFetched: string;
            fetchFailed: string;
            deleteConfirm: string;
            helpTitle: string;
            helpItems: string[];
        };
    };
    time: {
        justNow: string;
        minutesAgo: string;
        hoursAgo: string;
        daysAgo: string;
    };
    errors: {
        networkError: string;
        unauthorized: string;
        serverError: string;
        notFound: string;
        rateLimited: string;
    };
}
